import type { RoomState } from '@panels/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActivityReport } from './activity.js';
import { ApiError, getRoomState } from './api.js';

/**
 * The polling loop (§2, §8). There is no websocket anywhere in this app: party
 * mode is a single state endpoint, polled while a round is live and backed off
 * when nothing is happening. Polling stops entirely while the tab is hidden.
 */

const ACTIVE_INTERVAL = 2500;
const IDLE_INTERVAL = 6000;

/**
 * During a co-viewed reveal (Round 3). When the driver moves on, the replay
 * starts REVEAL_LEAD_MS later (shared/src/tuning.ts), and every follower has to
 * have heard about it by then: one poll interval plus a slow round trip.
 */
export const REVEAL_POLL_MS = 1000;

export interface RoomStateResult {
  state: RoomState | null;
  /** Local clock time the current state arrived, for ticking timers between polls. */
  receivedAt: number;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<RoomState | null>;
  /** Apply a state we already got back from a mutation, skipping a round-trip. */
  apply: (state: RoomState) => void;
}

export interface RoomStateOptions {
  /** The drawing screen reports its activity on every poll (Round 2 §1.1). */
  activity?: () => ActivityReport | null;
}

export function useRoomState(roomId: string | undefined, options: RoomStateOptions = {}): RoomStateResult {
  const [state, setState] = useState<RoomState | null>(null);
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  const activityRef = useRef(options.activity);
  activityRef.current = options.activity;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const stateRef = useRef<RoomState | null>(null);
  stateRef.current = state;

  const apply = useCallback((next: RoomState) => {
    setState(next);
    setReceivedAt(Date.now());
    setError(null);
    setLoading(false);
  }, []);

  const fetchOnce = useCallback(async (): Promise<RoomState | null> => {
    if (!roomId) return null;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const next = await getRoomState(roomId, controller.signal, activityRef.current?.());
      apply(next);
      return next;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      // A dropped poll is normal on a phone; keep the last good state on screen.
      const message = err instanceof ApiError ? err.message : 'Lost connection';
      setError(message);
      setLoading(false);
      return null;
    }
  }, [roomId, apply]);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;

    const schedule = () => {
      if (cancelled) return;
      const current = stateRef.current;
      // Between rounds, a ready-up can start the next round at any moment.
      const active = current?.round?.status === 'active' || !!current?.readyUpOpen;
      const delay = current?.reveal?.mode === 'coview' ? REVEAL_POLL_MS : active ? ACTIVE_INTERVAL : IDLE_INTERVAL;
      timerRef.current = window.setTimeout(tick, delay);
    };

    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') await fetchOnce();
      schedule();
    };

    setLoading(true);
    void fetchOnce().then(schedule);

    // Coming back to the tab should feel instant, not "wait for the next poll".
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchOnce();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [roomId, fetchOnce]);

  return { state, receivedAt, error, loading, refresh: fetchOnce, apply };
}
